import React from "react";
import type { IUploadCodeAndSourceSelectionStore } from "./UploadCodeAndSourceSelectionStore";
import { injectFromAppUpload } from "../../inject";
import { observer } from "mobx-react";
import { StaticNotificationComponent } from "../../../shared-components/static-notification/StaticNotificationComponent";
import OtpInput from "react-otp-input";
import { FloatingContainerComponent } from "../../../shared-components/floating-container/FloatingContainerComponent";
import Select from "react-dropdown-select";
import type { IResourcesStore } from "../../../resources/ResourcesStore";
import { injectFromApp } from "../../../shared-components/inject";
import { SourceLogoComponent } from "../../../shared-components/source-logo/SourceLogoComponent";
import { fireAndForget } from "../../../util/async/FireAndForget";

interface IUploadCodeAndSourceSelectionInvalidatedComponentProps {
    uploadCodeAndSourceSelectionStore?: IUploadCodeAndSourceSelectionStore;
    resourcesStore?: IResourcesStore;
}

@injectFromAppUpload<IUploadCodeAndSourceSelectionInvalidatedComponentProps>((stores) => ({
    uploadCodeAndSourceSelectionStore: stores.uploadCodeAndSourceSelectionStore,
}))

@injectFromApp<IUploadCodeAndSourceSelectionInvalidatedComponentProps>((stores) => ({
    resourcesStore: stores.resourcesStore,
}))

@observer
export class UploadCodeAndSourceSelectionInvalidatedComponent extends React.Component<IUploadCodeAndSourceSelectionInvalidatedComponentProps> {
    private readonly sourceChanged = (selectedOptions) => {
        const { uploadCodeAndSourceSelectionStore } = this.props;
        if (uploadCodeAndSourceSelectionStore!.validating) return;
        
        uploadCodeAndSourceSelectionStore!.setSelectedSource(!selectedOptions || selectedOptions.length === 0 ? null : selectedOptions[0]);
    };

    private readonly validateCodeAndSource = (event): void => {
        event.stopPropagation();
        const { uploadCodeAndSourceSelectionStore } = this.props;
        fireAndForget(uploadCodeAndSourceSelectionStore!.validateCodeAndSource());
    };

    private readonly uploadCodeChanged = (value): void => {
        const { uploadCodeAndSourceSelectionStore } = this.props;
        if (uploadCodeAndSourceSelectionStore!.validating) return;
        
        uploadCodeAndSourceSelectionStore!.setUploadCode(value.toUpperCase());
    };

    private readonly onFormSubmit = (event): void => {
        event.preventDefault();
    };

    render() {
        const { uploadCodeAndSourceSelectionStore, resourcesStore } = this.props;
        const { resources } = resourcesStore!;
        const {
            selectedSourceForUpload,
            sourcesAvailableForUpload,
            uploadCode,
            uploadCodeLength,
            validating,
            uploadCodeAndSourceValid,
            uploadCodeError,
            sourceSelectionError,
            uploadCodeLengthOk,
            expiredUploadCode,
        } = uploadCodeAndSourceSelectionStore!;

        if (uploadCodeAndSourceValid) return;

        return (
            <FloatingContainerComponent header={resources.UploadPortal} transparent={true} customClass={"invalidated-upload-code-and-source"}>
                <form onSubmit={this.onFormSubmit}>
                    <StaticNotificationComponent
                        customClass={""}
                        solid={true}
                        type={sourceSelectionError && !selectedSourceForUpload ? "error" : ""}
                        title={resources.HospitalInstitution}
                        hideIcon={true}
                    >
                        <Select
                            values={selectedSourceForUpload ? [selectedSourceForUpload] : []}
                            onChange={this.sourceChanged}
                            multi={false}
                            options={sourcesAvailableForUpload}
                            placeholder={resources.PleaseSelect}
                            labelField={"description"}
                            valueField={"id"}
                            searchBy={"description"}
                            clearable={false}
                            dropdownGap={2}
                            autoFocus={selectedSourceForUpload == null}
                            noDataLabel={resources.NoResultsFound}
                            backspaceDelete={true}
                            disabled={validating}
                        />

                        <div className={"logo-and-info"}>
                            <div className={"info"}>
                                {selectedSourceForUpload?.telephone && <div className={"telephone"}>{selectedSourceForUpload.telephone}</div>}
                                {selectedSourceForUpload?.email && <div className={"email"}>{selectedSourceForUpload.email}</div>}
                            </div>
                            <div className={"logo"}>
                                {selectedSourceForUpload?.logo && (
                                    <SourceLogoComponent logo={ selectedSourceForUpload.logo } logoStyle={ selectedSourceForUpload.logoStyle } description={ selectedSourceForUpload.description } />
                                )}
                            </div>
                        </div>

                        {selectedSourceForUpload && <p className={"small-text"}>{resources.UploadCodeContactHospital}</p>}
                    </StaticNotificationComponent>

                    <StaticNotificationComponent
                        customClass={"upload-code"}
                        solid={true}
                        type={uploadCodeError && !uploadCodeLengthOk ? "error" : ""}
                        title={resources.UploadCode}
                        hideIcon={true}
                    >
                        <OtpInput
                            value={uploadCode ?? ""}
                            onChange={this.uploadCodeChanged}
                            numInputs={uploadCodeLength}
                            renderSeparator={""}
                            renderInput={(props) => <input {...props} />}
                            shouldAutoFocus={uploadCode === ""}
                            containerStyle={"otp-container"}
                        />
                    </StaticNotificationComponent>

                    {uploadCodeAndSourceValid != null && !uploadCodeAndSourceValid && (
                        <StaticNotificationComponent solid={true} type={"error"} title={!expiredUploadCode ? resources.InvalidCombination : resources.ExpiredCombination}>
                            {!expiredUploadCode ? resources.UploadCodeInvalidCombination : resources.UploadCodeExpiredCombination}
                        </StaticNotificationComponent>
                    )}

                    <button disabled={validating} type={"submit"} className={"continue green" + (validating ? " loading" : "")} onClick={this.validateCodeAndSource}>
                        {resources.Continue}
                    </button>
                </form>
            </FloatingContainerComponent>
        );
    }
}
